import { useState } from 'react';

import { useCentralizedProviderData } from '@crehana/auth';
import { CrossPlatformNavigation } from '@crehana/compass.layouts';
import { LayoutWithNavbarAndSidebar } from '@crehana/compass.layouts/layout-with-navbar-and-sidebar';
import { UIThemeProvider } from '@crehana/compass.ui';
import { cn, PageLoader } from '@crehana/compass.ui/v2';
import {
  FeatureFlagProvider,
  useFeatureFlag,
} from '@crehana/feature-flags/react';
import { CrehanaNextSeo, type TAppProps } from '@crehana/next';
import { useIsClient } from '@crehana/react-hooks';
import {
  MainMenu,
  type MainMenuProps,
  PrivatePage,
  useMe,
  useUITheme,
} from '@crehana/web';
import { useRouter } from 'next/router';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { BrowserRouter } from 'react-router-dom';

import { B2BRootLayout } from '../LearnDashboard/components/B2BOrgLayout';
import useOrganizationTheme from '../LearnDashboard/hooks/useOrganizationTheme';
import { useWidgetsLayout } from './components/dynamic-grid-widgets/hooks/use-widgets-layout';
import { FixUserPathBySession } from './components/fix-user-path-by-session';
import { AMPLITUDE_PAGE_NAME } from './constants';
import Home from './home';
import { useHomeTranslations } from './hooks/use-home-translations';

// TODO: Remove this component when sidebar v1 is deprecated and no longer in use
// shouldSubtractHeight is used to subtract the height of the header (63px) from the height of the main content
const StyledMain: React.FC<
  React.PropsWithChildren<{
    className?: string;
    shouldSubtractHeight?: boolean;
  }>
> = ({ children, className, shouldSubtractHeight = true }) => (
  <div
    className={cn(
      shouldSubtractHeight ? 'tw-h-[calc(100dvh-63px)]' : 'tw-h-full',
      className,
    )}
  >
    {children}
  </div>
);

const Layout: React.FC = () => {
  const router = useRouter();
  const { orgSlug } = router.query;
  const organizationSlug = orgSlug as string;
  const { isDark } = useUITheme();
  const isClient = useIsClient();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { countryCode } = useHomeTranslations();
  const { loading, me: centralizedMe } = useCentralizedProviderData();
  const { loading: loadingWidgetsLayout, isFeatureFlagEnabled } =
    useWidgetsLayout();
  const { flagValue: isSidebarMenuEnabled, loading: loadingSidebarMenu } =
    useFeatureFlag('NEW_SIDEBAR_LAYOUT');
  const { flagValue: isSidebarV2Enabled, loading: loadingSidebarV2 } =
    useFeatureFlag('SIDEBAR_V2', 'V5');

  const handleRoute = (path: string) => {
    const url = path.replace('[slug]', organizationSlug);
    router.push(url);
  };

  const defaultMainMenuProps: MainMenuProps = {
    layoutConfig: {
      hideCatalogDropdown: true,
      hideSearch: true,
      hideResourcesDropdown: true,
      hideB2BDashboardButtonLink: true,
      hideDiplomasLink: true,
      hideCategories: true,
      hideNewCourses: true,
      hideUserDashboardLink: true,
      hideB2BAdminDashboardLink: false,
      hideSwitchToOldHomeUserLink: false,
      hideCrehanaCoinsButton: true,
      hideNotifications: true,
      embeddedInBreakpoints: ['sm', 'md', 'lg', 'xl'],
      leftLogoWrapperClassNames: 'hidden sm:flex',
      navType: 'dashboard',
      hideRightCatalog: true,
      hideMentorDashboardLink: true,
      newSidebarEnabled: true,
      hideMentorLandingLink: true,
      hideShoppingCart: true,
      hideInboxLink: true,
      hasProductsSwitch: true,
    },
    logoType: 'b2b',
    countryPrefix: countryCode!,
    amplitudePageName: AMPLITUDE_PAGE_NAME,
    embeddedIn: 'new-dashboard',
    theme: isDark ? 'dark' : 'light',
    customThemes: {
      light: {
        nav: {
          height: { xs: 56, sm: 62, md: 62, lg: 62 },
        },
      },
      dark: {
        nav: {
          height: { xs: 56, sm: 62, md: 62, lg: 62 },
        },
      },
    },
    onLeftMenuClick: () => {
      setMobileSidebarOpen(true);
    },
    organizationSlug: orgSlug as string,
    onNavigateUrl: handleRoute,
    isProduction: process.env.APP_ENV === 'production',
    hasIntegration: true,
    centralizeOrganizationId: centralizedMe?.organization.id,
  };

  if (
    loading ||
    loadingSidebarMenu ||
    loadingSidebarV2 ||
    loadingWidgetsLayout
  ) {
    return <PageLoader />;
  }

  const shouldSubtractHeight = !(isSidebarMenuEnabled && isSidebarV2Enabled);

  if (centralizedMe && isSidebarMenuEnabled) {
    return (
      <LayoutWithNavbarAndSidebar
        organizationSlug={centralizedMe.organization.slug}
        centralizedOrganizationId={centralizedMe.organization.id}
        viewMode="user"
        handledPathnames={[]}
        onHandledUrlClick={router.push}
      >
        <StyledMain
          className="tw-overflow-y-auto	tw-bg-neutral-light-200 dark:tw-bg-elevation-dark-00 tw-flex-grow"
          shouldSubtractHeight={shouldSubtractHeight}
        >
          <Home
            slug={organizationSlug}
            isDynamicPositioningWidgetsEnabled={isFeatureFlagEnabled}
          />
        </StyledMain>
      </LayoutWithNavbarAndSidebar>
    );
  }

  return (
    <div className="tw-h-screen tw-flex tw-flex-col">
      <nav className="tw-z-dialog tw-border-solid tw-border-b-[1px] tw-border-b-neutral-light-300 dark:tw-border-b-elevation-dark-04 tw-sticky tw-bg-neutral-light-100 dark:tw-bg-elevation-dark-01">
        <MainMenu {...defaultMainMenuProps} />
      </nav>
      <div className="tw-flex tw-flex-grow">
        {isClient && (
          <SidebarNavigation
            organizationSlug={organizationSlug}
            mobileSidebarOpen={mobileSidebarOpen}
            hideMobilesidebar={() => {
              setMobileSidebarOpen(false);
            }}
          />
        )}
        <StyledMain
          className="tw-overflow-y-auto	tw-bg-neutral-light-200 dark:tw-bg-elevation-dark-00 tw-flex-grow"
          shouldSubtractHeight={shouldSubtractHeight}
        >
          <Home
            slug={organizationSlug}
            isDynamicPositioningWidgetsEnabled={isFeatureFlagEnabled}
          />
        </StyledMain>
      </div>
    </div>
  );
};

const App: React.FC<React.PropsWithChildren<TAppProps>> = () => {
  const router = useRouter();
  const { orgSlug } = router.query;
  const organizationSlug = orgSlug as string;
  const { isDark } = useUITheme();
  // You can use a custom locale keys validated translator like the one at apps/learning-experience/src/views/LearnDashboard/hooks/useCrehanaTranslation.tsx
  const { languageCode } = useHomeTranslations();
  const { me } = useMe();
  const isB2B = !!me?.isB2B;

  const { organizationInfo, loading: loadingOrganizationInfo } =
    useOrganizationTheme({ organizationSlug });

  if (loadingOrganizationInfo) {
    return <PageLoader />;
  }

  return (
    <>
      <CrehanaNextSeo
        title="Home Dashboard"
        description="Certifícate, alcanza tus objetivos y mejora tus habilidades con nuestros packs de cursos con más de 40% de descuento. ¡Aprende hoy y aplica hoy!"
        hasCountryCode={false}
        nofollow
        noindex
      />
      <PrivatePage isDark={isDark} languageCode={languageCode}>
        <B2BRootLayout
          isB2B={isB2B}
          organizationSlug={organizationSlug}
          organizationInfo={organizationInfo}
        />
        <BrowserRouter>
          <UIThemeProvider>
            <FeatureFlagProvider
              organizationId={me?.organization?.originalId ?? 0}
              flags={[
                ['NEW_SIDEBAR_LAYOUT', 'V5'],
                ['DYNAMIC_POSITIONING_WIDGETS_UNIFIED_HOME', 'V5'],
                ['SIDEBAR_V2', 'V5'],
                ['ATTENDANCE_EMPLOYEE', 'V5'],
              ]}
            >
              <FixUserPathBySession>
                <Layout />
              </FixUserPathBySession>
            </FeatureFlagProvider>
          </UIThemeProvider>
        </BrowserRouter>
      </PrivatePage>
    </>
  );
};

export default App;

const SidebarNavigation = ({
  organizationSlug,
  mobileSidebarOpen,
  hideMobilesidebar,
}: {
  organizationSlug: string;
  mobileSidebarOpen: boolean;
  hideMobilesidebar(): void;
}) => {
  const { push } = useRouter();
  const { me } = useMe();
  const userOrganizationId = me?.organization?.originalId || undefined;

  return (
    <CrossPlatformNavigation
      email={me?.email || ''}
      organizationSlug={organizationSlug}
      sidebarType="collaborator"
      handleUrl={(url: string) => {
        // all hris lives in another microfrontend
        if (url.includes('hris/')) {
          window.location.href = url;
        }
        push(url);
      }}
      organizationId={userOrganizationId!}
      hideMobileSidebar={hideMobilesidebar}
      mobileSidebarOpen={mobileSidebarOpen}
      productPlatform="learning"
    />
  );
};
